import type React from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';
import InputError from '../../../Form/Error';
import CurrencyFormat from '../../../Form/InputFormat/CurrencyFormat';
import EinFormat from '../../../Form/InputFormat/EinFormat';
import NumericalFormat from '../../../Form/InputFormat/NumericalFormat';
import PhoneNumberFormat from '../../../Form/InputFormat/PhoneNumberFormat';
import SocialSecurityFormat from '../../../Form/InputFormat/SocialSecurityFormat';
import ZipCodeFormat from '../../../Form/InputFormat/ZipCodeFormat';
import type { HookInputBaseProps } from '../../types';

export type MaskedComponentType = 'currency' | 'ein' | 'numeric' | 'phone' | 'socialSecurity' | 'zipCode';
export interface HookMaskedInputProps<T extends FieldValues = FieldValues> extends HookInputBaseProps<T> {
  label?: string;
  name: FieldPath<T>;
  id?: string;
  required?: boolean;
  customClassName?: string;
  maskedComponent: MaskedComponentType;
}

type MaskedInputMapType = {
  [K in MaskedComponentType]: React.ElementType;
};
const maskedInputMap: MaskedInputMapType = {
  currency: CurrencyFormat,
  ein: EinFormat,
  numeric: NumericalFormat,
  phone: PhoneNumberFormat,
  socialSecurity: SocialSecurityFormat,
  zipCode: ZipCodeFormat,
};

const HookMaskedInput = <T extends FieldValues>({
  label,
  name,
  id,
  customClassName,
  validate,
  maskedComponent,
  required,
  formMethods,
}: HookMaskedInputProps<T>) => {
  const { control } = useFormContext<T>();
  const MaskComponent = maskedInputMap[maskedComponent];
  const idOrName = id || name;

  return (
    <div className={`${customClassName}`}>
      <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor={idOrName}>
        {label}
      </label>
      <Controller
        name={name}
        rules={{ required: required && 'Required', validate }}
        control={formMethods?.control || control}
        render={({ field, fieldState: { error } }) => (
          <>
            <MaskComponent field={field} id={idOrName} />
            {error && <InputError>{error.message}</InputError>}
          </>
        )}
      />
    </div>
  );
};
export default HookMaskedInput;
